import { Link } from 'react-router-dom'

const STEPS = [
  {
    num: '01',
    title: 'Compléter votre profil centre de formation',
    icon: '🏫',
    content: [
      'Dans <strong>Paramètres</strong>, renseignez votre numéro de déclaration d\'activité, votre adresse et votre logo.',
      'Si vous êtes certifié <strong>Qualiopi</strong>, activez le badge correspondant et uploadez votre certificat dans l\'onglet Documents & qualifications. Cela rend vos propositions visibles pour les demandes "Qualiopi requis".',
      'Un profil complet avec certification renforce la confiance des entreprises et augmente votre taux d\'acceptation.',
    ],
  },
  {
    num: '02',
    title: 'Consulter les demandes de formation',
    icon: '📬',
    content: [
      'L\'onglet <strong>Demandes formation</strong> liste toutes les demandes d\'entreprises en recherche de formateurs ou de centres.',
      'Chaque demande indique le sujet, le nombre de participants, la date souhaitée, le budget et la localisation.',
      'Un <strong>badge rouge</strong> dans la sidebar indique le nombre de nouvelles demandes disponibles depuis votre dernière visite.',
    ],
  },
  {
    num: '03',
    title: 'Soumettre une proposition tarifaire',
    icon: '💬',
    content: [
      'Cliquez sur une demande pour la déplier. Renseignez votre <strong>tarif en euros</strong> et un message de présentation (approche pédagogique, certifications, références).',
      'Votre proposition est envoyée à l\'entreprise. Elle peut l\'accepter, la refuser ou demander plus d\'informations via la messagerie.',
      'Vous retrouvez toutes vos propositions envoyées dans l\'onglet <strong>Mes propositions</strong>.',
    ],
  },
  {
    num: '04',
    title: 'Utiliser la recherche de formateur',
    icon: '🔍',
    content: [
      'Si vous avez une demande à honorer mais pas le formateur disponible, utilisez la fonction <strong>Rechercher un formateur</strong> sur la fiche demande.',
      'Renseignez l\'intitulé de la mission, la description, l\'adresse, la date et le budget. Vous pouvez sélectionner des spécialités requises (SST, extincteur, évacuation ERP…).',
      'Une mission est publiée sur Velink et les formateurs à proximité reçoivent une notification push. Vous pouvez suivre les candidatures dans <strong>Mes recherches formateurs</strong>.',
    ],
  },
  {
    num: '05',
    title: 'Gérer vos missions publiées',
    icon: '📋',
    content: [
      'L\'onglet <strong>Mes recherches formateurs</strong> liste toutes les missions TRAINING que vous avez publiées pour trouver des formateurs.',
      'Chaque carte affiche le statut (Ouverte, Assignée, Terminée), le nombre de formateurs notifiés et les candidatures reçues.',
      'Cliquez sur <strong>Voir la mission</strong> pour accéder au détail complet et contacter les candidats via le chat.',
    ],
  },
  {
    num: '06',
    title: 'Recevoir le paiement',
    icon: '💶',
    content: [
      'Si l\'entreprise accepte votre proposition, le statut passe en <strong>Assignée</strong> et le paiement est sécurisé en escrow.',
      'Après réalisation de la formation, l\'entreprise confirme la prestation. Le montant est libéré et versé sur votre compte Stripe Connect ou PayPal.',
      '<strong>Velink prélève 10 % de commission</strong> (à partir de la 2e mission de l\'entreprise cliente). Aucune commission côté centre de formation.',
    ],
  },
]

const FAQ = [
  { q: 'Quelle est la différence entre une "demande formation" et une "mission" sur Velink ?', a: 'Une demande formation est émise par une entreprise qui cherche un prestataire. Vous y répondez avec une proposition tarifaire. Une mission est publiée par vous (en tant que CF) pour recruter un formateur indépendant — c\'est l\'outil "Recherche formateur".' },
  { q: 'Mon badge Qualiopi est expiré. Comment le mettre à jour ?', a: 'Rendez-vous dans Documents & qualifications, supprimez l\'ancien certificat et uploadez le nouveau. Le badge reste actif pendant la durée de validité du certificat déclaré.' },
  { q: 'Puis-je proposer plusieurs tarifs pour la même demande ?', a: 'Non, une seule proposition par demande. Si votre proposition est déclinée, vous pouvez en soumettre une nouvelle (le formulaire réapparaît avec la mention "Renvoyer une proposition").' },
  { q: 'Les formateurs indépendants recrutés via "Recherche formateur" sont-ils vos employés ?', a: 'Non. Ils sont prestataires indépendants. Velink facilite la mise en relation mais n\'est pas partie au contrat de travail. Vous restez responsable du cadre contractuel avec eux.' },
  { q: 'Comment savoir si ma recherche de formateur a trouvé des candidats ?', a: 'Le badge "Mission publiée" apparaît sur la fiche demande. Dans "Mes recherches formateurs", la colonne "Candidatures" indique le nombre de prestataires ayant accepté.' },
]

export default function GuideCFPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-[#F59E0B] text-xs font-medium mb-4">
          Guide centre de formation — Flux complet
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-2">Comment utiliser Velink en tant que centre de formation</h1>
        <p className="text-text-secondary text-sm leading-relaxed max-w-2xl">
          Velink vous permet de répondre aux demandes de formation des entreprises, mais aussi de recruter des formateurs
          indépendants pour honorer vos commandes. Ce guide couvre les deux flux.
        </p>
      </div>

      {/* Dual flow banner */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card p-4 rounded-xl border-l-2 border-l-accent">
          <p className="text-accent font-semibold text-sm mb-1">Flux 1 — Répondre aux entreprises</p>
          <p className="text-text-secondary text-xs leading-relaxed">Une entreprise publie une demande → vous proposez votre tarif → elle accepte → prestation + paiement.</p>
        </div>
        <div className="glass-card p-4 rounded-xl border-l-2 border-l-success">
          <p className="text-success font-semibold text-sm mb-1">Flux 2 — Recruter des formateurs</p>
          <p className="text-text-secondary text-xs leading-relaxed">Vous avez une commande → vous publiez une mission → les formateurs postulent → vous choisissez.</p>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {STEPS.map((step) => (
          <div key={step.num} className="glass-card rounded-xl overflow-hidden">
            <div className="flex items-center gap-4 p-5">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/25 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">{step.icon}</span>
              </div>
              <div className="flex-1">
                <div className="mb-1">
                  <span className="text-xs font-mono text-text-muted">Étape {step.num}</span>
                </div>
                <h2 className="text-text-primary font-semibold">{step.title}</h2>
              </div>
            </div>
            <div className="px-5 pb-5 border-t border-white/[0.06] pt-4">
              <ul className="space-y-2">
                {step.content.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-text-secondary text-sm leading-relaxed">
                    <span className="text-[#F59E0B] mt-0.5 flex-shrink-0">→</span>
                    <span dangerouslySetInnerHTML={{ __html: line }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Qualiopi info */}
      <div className="glass-card p-5 rounded-xl flex items-start gap-4">
        <span className="text-2xl">🏅</span>
        <div>
          <p className="text-text-primary font-semibold text-sm mb-2">Certification Qualiopi</p>
          <p className="text-text-secondary text-sm leading-relaxed mb-3">
            Le badge Qualiopi est affiché sur votre profil et vous donne accès aux demandes qui l'exigent.
            Sans certification, vous ne voyez pas les demandes marquées "Qualiopi requis".
          </p>
          <Link to="/cf-documents" className="glass-btn px-4 py-2 text-sm rounded-xl inline-block">
            Gérer mes documents →
          </Link>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="glass-card p-5 rounded-xl">
        <h2 className="text-text-primary font-semibold mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/cf-formations" className="glass-btn-accent px-4 py-3 text-sm rounded-xl text-center block">
            Voir les demandes formation
          </Link>
          <Link to="/cf-mes-missions" className="glass-btn px-4 py-3 text-sm rounded-xl text-center block">
            Mes recherches formateurs
          </Link>
          <Link to="/cf-documents" className="glass-btn px-4 py-3 text-sm rounded-xl text-center block">
            Documents & Qualiopi
          </Link>
          <Link to="/cf-parametres" className="glass-btn px-4 py-3 text-sm rounded-xl text-center block">
            Paramètres CF
          </Link>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <h2 className="text-text-primary font-semibold mb-4">Questions fréquentes</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div key={i} className="glass-card p-5 rounded-xl">
              <p className="text-text-primary font-medium text-sm mb-2">{item.q}</p>
              <p className="text-text-secondary text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="glass-card p-5 rounded-xl flex items-start gap-4">
        <span className="text-2xl">✉️</span>
        <div>
          <p className="text-text-primary font-semibold text-sm mb-1">Besoin d'aide ?</p>
          <p className="text-text-secondary text-sm">
            Contactez notre équipe à{' '}
            <a href="mailto:contact@velink.fr" className="text-accent hover:underline">contact@velink.fr</a>.
            Nous répondons sous 24 h (jours ouvrés).
          </p>
        </div>
      </div>
    </div>
  )
}
